import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';
import './UserProfilePage.css';

const UserProfilePage = () => {
  const { userId } = useParams();
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [userDesigns, setUserDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designsLoading, setDesignsLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchUserDesigns();
      setIsOwnProfile(currentUser && currentUser.uid === userId);
    }
  }, [userId, currentUser]);

  const fetchUserData = async () => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setUserData(userDocSnap.data());
      } else {
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDesigns = async () => {
    try {
      const designsRef = collection(db, 'designs');
      const q = query(
        designsRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const designs = [];
      querySnapshot.forEach((doc) => {
        designs.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setUserDesigns(designs);
    } catch (error) {
      console.error("Error fetching user designs: ", error);
    } finally {
      setDesignsLoading(false);
    }
  };

  const DesignCard = ({ design }) => (
    <Link to={`/gallery/${design.id}`} className="consistent-design-card">
      <div className="design-image">
        <img src={design.imageUrl} alt={design.name} />
      </div>
      <div className="design-info">
        <h4 className="design-name">{design.name || 'No Name'}</h4>
        {design.county && design.state && (
          <p className="design-location">
            {design.county}, {design.state}
          </p>
        )}
        <p className="design-date">
          {design.createdAt?.toDate().toLocaleDateString()}
        </p>
        <div className="design-stats">
          <span className="vote-count">❤️ {design.voteCount || 0}</span>
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return <div className="user-profile-loading">Loading...</div>;
  }

  if (!userData) {
    return (
      <div className="user-profile-error">
        <h2>User Not Found</h2>
        <p>The user you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="page-header">
        <h1>{userData.displayName || 'Anonymous Designer'}</h1>
        {userData.createdAt && (
          <p className="subtitle">
            Member since {userData.createdAt.toDate().toLocaleDateString()}
          </p>
        )}
        {isOwnProfile && (
          <div className="profile-actions">
            <a href="/profile" className="edit-profile-btn">Edit Profile</a>
          </div>
        )}
      </div>

      <div className="user-designs-section">
        <h2>Designs by {userData.displayName || 'Anonymous Designer'}</h2>
        {designsLoading ? (
          <div className="designs-loading">Loading designs...</div>
        ) : userDesigns.length > 0 ? (
          <div className="user-designs-grid">
            {userDesigns.map((design) => (
              <DesignCard key={design.id} design={design} />
            ))}
          </div>
        ) : (
          <div className="no-designs">
            <p>No designs found for this user.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage; 